import React, { Component, useRef, useEffect } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { sidebarCssClasses } from "./Shared";

import "./Shared/element-closest";
import LayoutHelper from "./Shared/layout/layout";

const ClickOutHandler = ({ children, onClickOut }) => {
  const elementRef = useRef(null);

  useEffect(() => {
    let elTouchIsClick = true;
    let documentTouchIsClick = true;
    const el = elementRef.current;

    const elementTouchMoved = () => {
      elTouchIsClick = false;
    };

    const elementTouchEnded = (e) => {
      if (elTouchIsClick) elementClicked(e);
      elTouchIsClick = true;
    };

    const elementClicked = (e) => {
      e.__clickedElements = e.__clickedElements || [];
      e.__clickedElements.push(el);
    };

    const documentTouchStarted = () => {
      const eventClickedIndex = el
        ? el.removeEventListener("click", elementClicked)
        : null;
      document.removeEventListener("click", documentClicked);
    };

    const documentTouchMoved = () => {
      documentTouchIsClick = false;
    };

    const documentTouchEnded = (e) => {
      if (documentTouchIsClick) documentClicked(e);
      documentTouchIsClick = true;
    };

    const documentClicked = (e) => {
      if ((e.__clickedElements || []).indexOf(el) !== -1) return;

      if (onClickOut) {
        onClickOut(e);
      } else {
        console.warn("onClickOut is not defined.");
      }
    };

    const addEventListeners = () => {
      if (el) {
        el.addEventListener("touchmove", elementTouchMoved);
        el.addEventListener("touchend", elementTouchEnded);
        el.addEventListener("click", elementClicked);
      }
      document.addEventListener("touchstart", documentTouchStarted);
      document.addEventListener("touchmove", documentTouchMoved);
      document.addEventListener("touchend", documentTouchEnded);
      document.addEventListener("click", documentClicked);
    };

    const removeEventListeners = () => {
      if (el) {
        el.removeEventListener("touchmove", elementTouchMoved);
        el.removeEventListener("touchend", elementTouchEnded);
        el.removeEventListener("click", elementClicked);
      }
      document.removeEventListener("touchstart", documentTouchStarted);
      document.removeEventListener("touchmove", documentTouchMoved);
      document.removeEventListener("touchend", documentTouchEnded);
      document.removeEventListener("click", documentClicked);
    };

    addEventListeners();

    return () => {
      removeEventListeners();
    };
  }, [onClickOut]);

  // Render the element and use the ref to access it
  return (
    <div ref={elementRef}>
      {Array.isArray(children) ? (
        <div>{children}</div>
      ) : (
        React.Children.only(children)
      )}
    </div>
  );
};

// Adding PropTypes for validation
ClickOutHandler.propTypes = {
  children: PropTypes.node.isRequired,
  onClickOut: PropTypes.func.isRequired,
};

const propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  compact: PropTypes.bool,
  display: PropTypes.string,
  fixed: PropTypes.bool,
  minimized: PropTypes.bool,
  isOpen: PropTypes.bool,
  offCanvas: PropTypes.bool,
  staticContext: PropTypes.any,
  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
};

const defaultProps = {
  tag: "div",
  compact: false,
  display: "",
  fixed: false,
  minimized: false,
  isOpen: false,
  offCanvas: false,
};

class AppSidebar extends Component {
  constructor(props) {
    super(props);

    this.isCompact = this.isCompact.bind(this);
    this.isFixed = this.isFixed.bind(this);
    this.isMinimized = this.isMinimized.bind(this);
    this.isOffCanvas = this.isOffCanvas.bind(this);
    this.displayBreakpoint = this.displayBreakpoint.bind(this);
    this.hideMobile = this.hideMobile.bind(this);
  }

  componentDidMount() {
    this.displayBreakpoint(this.props.display);
    this.isCompact(this.props.compact);
    this.isFixed(this.props.fixed);
    this.isMinimized(this.props.minimized);
    this.isOffCanvas(this.props.offCanvas);
  }

  isCompact(compact) {
    if (compact) {
      document.body.classList.add("sidebar-compact");
    }
  }

  isFixed(fixed) {
    if (fixed) {
      document.body.classList.add("sidebar-fixed");
    }
  }

  isMinimized(minimized) {
    LayoutHelper.sidebarToggle(minimized);
  }

  isOffCanvas(offCanvas) {
    if (offCanvas) {
      document.body.classList.add("sidebar-off-canvas");
    }
  }

  displayBreakpoint(display) {
    const cssTemplate = `sidebar-${display}-show`;
    let [cssClass] = sidebarCssClasses[0];
    if (display && sidebarCssClasses.indexOf(cssTemplate) > -1) {
      cssClass = cssTemplate;
    }
    document.body.classList.add(cssClass);
  }

  hideMobile() {
    if (document.body.classList.contains("sidebar-show")) {
      document.body.classList.remove("sidebar-show");
    }
  }

  onClickOut(e) {
    if (
      typeof window !== "undefined" &&
      document.body.classList.contains("sidebar-show")
    ) {
      if (!e.target.closest("[data-sidebar-toggler]")) {
        this.hideMobile();
      }
    }
  }

  render() {
    const { className, children, tag: Tag, ...attributes } = this.props;

    delete attributes.compact;
    delete attributes.display;
    delete attributes.fixed;
    delete attributes.minimized;
    delete attributes.offCanvas;
    delete attributes.isOpen;
    delete attributes.staticContext;

    const classes = classNames(className, "sidebar");

    // sidebar-nav root
    return (
      <ClickOutHandler
        onClickOut={(e) => {
          this.onClickOut(e);
        }}
      >
        <Tag className={classes} {...attributes}>
          {children}
        </Tag>
      </ClickOutHandler>
    );
  }
}

AppSidebar.propTypes = propTypes;
AppSidebar.defaultProps = defaultProps;

export default AppSidebar;
